/**
 * Die inhaltlichen Kategorien der Seitenleiste — eine Ebene über den Gruppen.
 *
 * Wer eine Liegenschaft beurteilt, sucht nach einem Thema („Wohnen“, „Umwelt“), nicht nach einer Quelle. Deshalb
 * stehen hier Zensus-Themen, INKAR-Tabellen, POI-Gruppen und Kartenebenen gemischt beieinander: die Miete aus dem
 * Zensus neben dem Wohnungsbestand aus INKAR, die Schulen (POI) neben der Bildungsstatistik.
 *
 * Neue Zensus-Themen und INKAR-Tabellen kommen aus dem Katalog des Servers; was hier nicht zugeordnet ist, landet in
 * `population` (Zensus) bzw. `other`, statt zu verschwinden.
 */

export type CategoryKey =
    | 'planning'
    | 'housing'
    | 'population'
    | 'economy'
    | 'mobility'
    | 'social'
    | 'environment'
    | 'safety'
    | 'other';

/** Reihenfolge in der Liste: was eine Fläche bestimmt zuerst, was ihr Umfeld beschreibt danach. */
export const CATEGORY_ORDER: CategoryKey[] = [
    'planning',
    'housing',
    'population',
    'economy',
    'mobility',
    'social',
    'environment',
    'safety',
    'other',
];

/** Zensus: Thema → Kategorie über dieselben Schlüsselwörter wie die Symbole (groupIcons). */
const ZENSUS_RULES: [CategoryKey, RegExp][] = [
    ['housing', /miete|eigentuemer|leerstand|kaufwert|heizung|energietraeger|brennstoff|gebaeude|wohnung|wohnraum|baujahr|flaeche|raeume|zimmer/],
    ['population', /haushalt|familie|lebensform|senioren|alter|jaehrige|bevoelkerung|staatsangehoerig|auslaender|deutsche|geburtsland|religion|familienstand|geschlecht/],
    ['economy', /erwerb|beruf|stellung|bildung|schulabschluss/],
];

export const zensusCategory = (theme: string): CategoryKey =>
    ZENSUS_RULES.find(([, pattern]) => pattern.test(theme))?.[0] ?? 'population';

const INKAR: Record<string, CategoryKey> = {
    PopulationData: 'population',
    BuildingAndHousingData: 'housing',
    EconomicsData: 'economy',
    IncomeData: 'economy',
    EmploymentData: 'economy',
    PublicFinancesData: 'economy',
    TrafficData: 'mobility',
    SocialSupplyData: 'social',
    SocialBenefitsData: 'social',
    SubsidiesData: 'social',
    SubjectOfStudy: 'social',
    StudentsNationalityGender: 'social',
    LandUseData: 'planning',
    EnvironmentData: 'environment',
    CrimeStatistics: 'safety',
    PoliticsData: 'safety',
};

export const inkarCategory = (type: string): CategoryKey => INKAR[type] ?? 'other';

const POI: Record<string, CategoryKey> = {
    infrastructure: 'mobility',
    mobility: 'mobility',
    education: 'social',
    health: 'social',
    safety: 'safety',
    commerce: 'economy',
    environment: 'environment',
};

export const poiCategory = (group: string): CategoryKey => POI[group] ?? 'other';

/** Die festen Gruppen der Seite (Kartenebenen). */
const FIXED: Record<string, CategoryKey> = {
    layers: 'planning',
    planning: 'planning',
    power: 'mobility',
    ways: 'mobility',
    bridges: 'mobility',
    hazards: 'environment',
    climate: 'environment',
    nature: 'environment',
};

export const fixedCategory = (id: string): CategoryKey => FIXED[id] ?? 'other';
